import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { renderAsync } from 'docx-preview';
import { Loader2, FileText, Eye, EyeOff, Check, X, ChevronDown, ChevronUp, Edit, Plus, Trash2, ZoomIn, ZoomOut, RotateCcw, GitCompare, PenLine } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { QAgentSuggestion } from '../../../services/QAgent';
import { cn } from '@/lib/utils';
import { useI18n } from '../../../i18n';
import { useScaledHeight } from '@/hooks/use-scaled-height';
import { changedSpan } from './changed-span';
import { CardChange, changeRanges, insertRoles, isClauseHeading, isQuotedTerm } from './card-changes';
import { insertionPointAfter, markInsertedAfter } from './insert-position';
import JSZip from 'jszip';
import {
  applyWordListNumbers,
  ListDefinitions,
  listStylesForAllInstances,
  parseNumbering,
  withParagraphNumbering,
} from './word-numbering';
import { findParagraph, findSubstring as findSubstringMatch, preserveQuoteStyle as preserveQuoteStyleFn } from '@/utils/text-matcher';

interface SuggestionDocumentViewerProps {
  fileUrl: string;
  suggestions: QAgentSuggestion[];
  scrollToSuggestionId?: string | null;
  onScrollComplete?: () => void;
  onAcceptSuggestion: (suggestionId: string) => void;
  onRejectSuggestion: (suggestionId: string) => void;
  onUndoSuggestion?: (suggestionId: string) => void;
  /** Called with the ids of accepted suggestions that could not be located /
   *  applied to the rendered document, so the parent can warn the user. */
  onSuggestionsApplyFailed?: (suggestionIds: string[]) => void;
  disabled?: boolean;
}

// --- Text-content paragraph search helpers ---

function findParagraphByText(
  allParagraphs: Map<number, HTMLElement>,
  searchText: string,
  hintIndex: number,
): HTMLElement | null {
  if (!searchText) return null;

  const paragraphs: Array<{ index: number; text: string }> = [];
  allParagraphs.forEach((el, idx) => {
    paragraphs.push({ index: idx, text: el.textContent || '' });
  });

  const result = findParagraph(paragraphs, searchText, hintIndex);
  if (!result) return null;
  return allParagraphs.get(result.index) || null;
}

/** Resolve the index key in indexedParagraphs for a given element, or return fallback. */
function findIndexForElement(
  indexedParagraphs: Map<number, HTMLElement>,
  el: HTMLElement | null,
  fallback: number,
): number {
  if (!el) return fallback;
  for (const [idx, pEl] of indexedParagraphs) {
    if (pEl === el) return idx;
  }
  return fallback;
}

/** Get the search text for locating a suggestion's target paragraph.
 *  For deletes, oldText may contain multiple paragraphs (heading + body
 *  joined with newlines). We only need the first line (heading) to find
 *  the target <p> element in the rendered document. */
function getSuggestionSearchText(s: QAgentSuggestion): string {
  if (s.type === 'insert') return s.textBefore || "";
  const text = s.oldText || "";
  // For multi-paragraph oldText, use only the first line
  const firstLine = text.split(/\n/)[0].trim();
  return firstLine || text;
}

// --- Suggestion card helpers ---

const getTypeIcon = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return <Edit className="h-3 w-3" />;
    case 'insert':
      return <Plus className="h-3 w-3" />;
    case 'delete':
      return <Trash2 className="h-3 w-3" />;
  }
};

const getTypeColor = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return 'border-blue-300 bg-blue-50';
    case 'insert':
      return 'border-green-300 bg-green-50';
    case 'delete':
      return 'border-red-300 bg-red-50';
  }
};

const getTypeBadgeColor = (type: QAgentSuggestion['type']) => {
  switch (type) {
    case 'edit':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'insert':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'delete':
      return 'bg-red-100 text-red-700 border-red-200';
  }
};

// --- Inline suggestion cards rendered via portal ---

interface SuggestionCardsProps {
  suggestions: QAgentSuggestion[];
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  disabled: boolean;
}

const SuggestionCards: React.FC<SuggestionCardsProps> = ({ suggestions, onAccept, onReject, disabled }) => {
  const { t } = useI18n();
  const st = t.qagentDocument.suggestions;
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(new Set());
  // Auto-expand pending suggestions when they first appear, without
  // re-expanding ones the user has manually collapsed
  const seenSuggestionIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const newPendingIds = suggestions
      .filter(s => s.status === 'pending' && !seenSuggestionIdsRef.current.has(s.id))
      .map(s => s.id);
    suggestions.forEach(s => seenSuggestionIdsRef.current.add(s.id));
    if (newPendingIds.length === 0) return;
    setExpandedSuggestions(prev => {
      const next = new Set(prev);
      newPendingIds.forEach(id => next.add(id));
      return next;
    });
  }, [suggestions]);

  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  if (pendingSuggestions.length === 0) return null;

  const toggleSuggestion = (id: string) => {
    setExpandedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <>
      {pendingSuggestions.map((suggestion) => {
        const isExpanded = expandedSuggestions.has(suggestion.id);

        return (
          <div
            key={suggestion.id}
            className={cn(
              "mt-2 rounded-lg border-2 transition-all",
              getTypeColor(suggestion.type),
            )}
          >
            <button
              onClick={() => toggleSuggestion(suggestion.id)}
              className="w-full flex items-center justify-between p-2 text-left hover:bg-white/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn("flex items-center gap-1 text-xs", getTypeBadgeColor(suggestion.type))}
                >
                  {getTypeIcon(suggestion.type)}
                  <span className="capitalize">{st.types[suggestion.type] ?? suggestion.type}</span>
                </Badge>
              </div>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>

            {isExpanded && (
              <div className="px-3 pb-3 space-y-2">
                <div className="text-sm space-y-1">
                  {suggestion.type === 'edit' && (
                    <>
                      {suggestion.oldText && (
                        <div className="bg-red-100 border border-red-200 rounded px-2 py-1">
                          <span className="text-red-700 line-through">{suggestion.oldText}</span>
                        </div>
                      )}
                      {suggestion.newText && (
                        <div className="bg-green-100 border border-green-200 rounded px-2 py-1">
                          <span className="text-green-700">{suggestion.newText}</span>
                        </div>
                      )}
                    </>
                  )}

                  {suggestion.type === 'insert' && suggestion.newText && (
                    <div className="bg-green-100 border border-green-200 rounded px-2 py-1">
                      <span className="text-green-700">+ {suggestion.newText}</span>
                    </div>
                  )}

                  {suggestion.type === 'delete' && suggestion.oldText && (
                    <div className="bg-red-100 border border-red-200 rounded px-2 py-1">
                      <span className="text-red-700 line-through">{suggestion.oldText}</span>
                    </div>
                  )}
                </div>

                {suggestion.comment && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground bg-white/70 rounded px-2 py-1.5">
                    <span className="text-sm">💡</span>
                    <span>{suggestion.comment}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAccept(suggestion.id);
                    }}
                    disabled={disabled}
                    className="h-7 gap-1.5 text-green-600 border-green-200 hover:bg-green-50 bg-white"
                  >
                    <Check className="h-3.5 w-3.5" />
                    {st.accept}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onReject(suggestion.id);
                    }}
                    disabled={disabled}
                    className="h-7 gap-1.5 text-red-600 border-red-200 hover:bg-red-50 bg-white"
                  >
                    <X className="h-3.5 w-3.5" />
                    {st.reject}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

// --- Insert helpers ---
// docx-preview renders headings and body text as <p> elements with inline
// styles, tabs, and multi-span structures. To faithfully insert new content,
// we deep-clone a matching paragraph and replace only its text.

const SECTION_NUM_RE = /^(\d+(?:\.\d+)*)/;

/** Count heading depth: "10" → 1, "10.2" → 2, "10.2.1" → 3 */
function headingLevel(text: string): number {
  const m = text.trim().match(SECTION_NUM_RE);
  if (!m) return 0;
  return m[1].split('.').length;
}

/**
 * Find a paragraph in the document whose heading level matches `level`.
 * Prefers paragraphs near `nearIdx` in the indexed map.
 */
function findHeadingDonor(
  indexedParagraphs: Map<number, HTMLElement>,
  level: number,
  nearIdx: number,
): HTMLElement | null {
  let best: HTMLElement | null = null;
  let bestDist = Infinity;

  indexedParagraphs.forEach((el, idx) => {
    const text = el.textContent?.trim() || '';
    if (headingLevel(text) === level) {
      const dist = Math.abs(idx - nearIdx);
      if (dist < bestDist) {
        bestDist = dist;
        best = el;
      }
    }
  });

  return best;
}

/** Find a body-text paragraph (non-heading, non-trivial length) near `nearIdx`. */
function findBodyDonor(
  indexedParagraphs: Map<number, HTMLElement>,
  nearIdx: number,
): HTMLElement | null {
  let best: HTMLElement | null = null;
  let bestDist = Infinity;

  indexedParagraphs.forEach((el, idx) => {
    const text = el.textContent?.trim() || '';
    if (!SECTION_NUM_RE.test(text) && text.length > 30) {
      const dist = Math.abs(idx - nearIdx);
      if (dist < bestDist) {
        bestDist = dist;
        best = el;
      }
    }
  });

  return best;
}

/** Detect capitalization pattern of a string */
function detectCase(text: string): 'upper' | 'title' | 'lower' | 'mixed' {
  if (text === text.toUpperCase() && text !== text.toLowerCase()) return 'upper';
  // Title case: first letter of each significant word is capitalized
  const words = text.split(/\s+/);
  const isTitleCase = words.every(
    w => w.length <= 3 || (w[0] === w[0].toUpperCase() && w.slice(1) === w.slice(1).toLowerCase()),
  );
  if (isTitleCase && words.some(w => w[0] === w[0].toUpperCase())) return 'title';
  if (text === text.toLowerCase()) return 'lower';
  return 'mixed';
}

/** Apply a capitalization pattern to text */
function applyCase(text: string, pattern: 'upper' | 'title' | 'lower' | 'mixed'): string {
  switch (pattern) {
    case 'upper':
      return text.toUpperCase();
    case 'lower':
      return text.toLowerCase();
    case 'title':
      return text.replace(/\w\S*/g, (w) =>
        w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
      );
    default:
      return text;
  }
}

/**
 * Deep-clone a donor paragraph and replace its text with `newText`,
 * preserving the DOM structure (spans, tabs, inline styles) and matching
 * the donor's formatting patterns (capitalization, number/title separation).
 *
 * For headings (text starting with a section number like "10.2"):
 * - Parses the donor to find: number portion, separator (tab/spaces), title portion
 * - Parses newText to extract the new number and title
 * - Replaces each part independently, preserving separator characters
 * - Matches the donor title's capitalization (ALL CAPS, Title Case, etc.)
 *
 * For non-headings: simple text replacement into first text node.
 */
function cloneParagraphWithNewText(donor: HTMLElement, newText: string): HTMLElement {
  const clone = donor.cloneNode(true) as HTMLElement;
  const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  if (textNodes.length === 0) {
    clone.textContent = newText;
    return clone;
  }

  // Get the donor's full text and parse its structure
  const donorFullText = textNodes.map(n => n.textContent || '').join('');
  const donorHeadingMatch = donorFullText.match(/^(\d+(?:\.\d+)*)([\s\t]+)([\s\S]*)$/);
  const newHeadingMatch = newText.match(/^(\d+(?:\.\d+)*)\s+([\s\S]*)$/);

  let finalText = newText;

  if (donorHeadingMatch && newHeadingMatch) {
    // Both are headings - preserve the donor's separator and capitalization
    const donorSeparator = donorHeadingMatch[2]; // tab, spaces, etc.
    const donorTitle = donorHeadingMatch[3].trim();
    const newNumber = newHeadingMatch[1];
    const newTitle = newHeadingMatch[2].trim();

    // Match the donor's capitalization pattern
    const casePattern = detectCase(donorTitle);
    const formattedTitle = applyCase(newTitle, casePattern);
    finalText = newNumber + donorSeparator + formattedTitle;
  }

  // Put all text in the first text node, clear the rest.
  // This matches the backend export logic and avoids fragile
  // character-by-character text node distribution.
  textNodes[0].textContent = finalText;
  for (let i = 1; i < textNodes.length; i++) {
    textNodes[i].textContent = '';
  }

  return clone;
}

// --- DOM text replacement helper ---
// docx-preview splits text across multiple <span> elements inside a <p>.
// We walk all text nodes, concatenate them, find the target substring, then
// splice the replacement into the correct text node(s).

// Known limitation: if two edits target the same paragraph, the offset tracking
// after a replacement uses the original node length rather than the modified
// length, which can cause incorrect splicing for the second edit. This is
// acceptable because each suggestion is applied in a separate call.
/**
 * Replace `oldText` with `newText` inside an element's text nodes.
 * Returns true only if the replacement was actually applied, so callers can
 * tell the user when an accepted change could not be placed instead of
 * silently pretending it succeeded.
 */
function replaceTextInElement(element: HTMLElement, oldText: string, newText: string): boolean {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let fullText = '';

  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    textNodes.push(node);
    fullText += node.textContent || '';
  }

  // Use smart matching pipeline: exact → normalized → fuzzy → prefix
  const match = findSubstringMatch(fullText, oldText);
  if (!match.found || !match.matchedText) return false;

  // A prefix match only covers the first part of oldText; its matchedText is
  // just that prefix. Swapping the prefix span for the full newText would
  // leave the tail of the old text behind and garble the paragraph, so refuse
  // it here and let the caller report a failed apply rather than corrupt text.
  if (match.confidence === 'prefix') return false;

  const startIdx = fullText.indexOf(match.matchedText);
  if (startIdx === -1) return false;

  // If matched via normalization, preserve quote style in replacement
  const finalNewText = match.confidence !== 'exact'
    ? preserveQuoteStyleFn(match.matchedText, newText)
    : newText;

  const endIdx = startIdx + match.matchedText.length;

  // Find which text nodes span [startIdx, endIdx) and replace
  let offset = 0;
  for (const node of textNodes) {
    const nodeLen = (node.textContent || '').length;
    const nodeStart = offset;
    const nodeEnd = offset + nodeLen;

    if (nodeEnd <= startIdx || nodeStart >= endIdx) {
      // Node is entirely outside the replacement range
      offset += nodeLen;
      continue;
    }

    const replaceStart = Math.max(0, startIdx - nodeStart);
    const replaceEnd = Math.min(nodeLen, endIdx - nodeStart);
    const before = (node.textContent || '').substring(0, replaceStart);
    const after = (node.textContent || '').substring(replaceEnd);

    // Only insert newText in the first overlapping node
    if (nodeStart <= startIdx) {
      node.textContent = before + finalNewText + after;
    } else {
      node.textContent = before + after;
    }

    offset += nodeLen;
  }

  return true;
}

/**
 * Show an accepted edit as a tracked change (Word's "All Markup"): the removed
 * words struck through, the added words underlined after them. Same matching
 * as replaceTextInElement; false when the old text can't be located.
 */
function markTextChangeInElement(element: HTMLElement, oldText: string, newText: string): boolean {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let fullText = '';
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    textNodes.push(node);
    fullText += node.textContent || '';
  }

  const match = findSubstringMatch(fullText, oldText);
  if (!match.found || !match.matchedText || match.confidence === 'prefix') return false;
  const matchStart = fullText.indexOf(match.matchedText);
  if (matchStart === -1) return false;

  const finalNewText = match.confidence !== 'exact'
    ? preserveQuoteStyleFn(match.matchedText, newText)
    : newText;
  const span = changedSpan(match.matchedText, finalNewText);
  if (!span.removed && !span.added) return true;

  // Text offset -> DOM position
  const positionAt = (index: number): { node: Text; offset: number } | null => {
    let offset = 0;
    for (const node of textNodes) {
      const len = (node.textContent || '').length;
      if (index <= offset + len) return { node, offset: index - offset };
      offset += len;
    }
    return null;
  };
  const from = positionAt(matchStart + span.start);
  const to = positionAt(matchStart + span.end);
  if (!from || !to) return false;
  // docx-preview puts the font on each run's span, so inserted words go in
  // a copy of the run they replace; otherwise they take the app's font
  const runEl = from.node.parentElement;
  const run = runEl && runEl !== element && element.contains(runEl) ? runEl : null;

  const range = document.createRange();
  range.setStart(from.node, from.offset);
  range.setEnd(to.node, to.offset);

  let anchor: Node;
  if (span.removed) {
    const del = document.createElement('del');
    del.className = 'tracked-del';
    del.appendChild(range.extractContents());
    range.insertNode(del);
    anchor = del;
  } else {
    // Pure insertion: an empty marker at the position to insert after
    const marker = document.createElement('span');
    range.insertNode(marker);
    anchor = marker;
  }
  if (span.added) {
    const ins = document.createElement('ins');
    ins.className = 'tracked-ins';
    if (run) {
      const runCopy = run.cloneNode(false) as HTMLElement;
      runCopy.textContent = span.added;
      ins.appendChild(runCopy);
    } else {
      ins.textContent = span.added;
    }
    anchor.parentNode?.insertBefore(ins, anchor.nextSibling);
  }
  return true;
}

/**
 * Draw an accepted edit with the changes the Word redline makes for it (sent
 * by the backend, see card-changes): with markup the deleted words struck
 * and the inserted words underlined, without markup the changes applied.
 * Unchanged text keeps the document's own characters. False when the old
 * text can't be located or the changes don't fit it (the caller falls back).
 */
function applyCardChangesInElement(
  element: HTMLElement,
  oldText: string,
  changes: CardChange[],
  markup: boolean,
): boolean {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let fullText = '';
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    textNodes.push(node);
    fullText += node.textContent || '';
  }

  const match = findSubstringMatch(fullText, oldText);
  if (!match.found || !match.matchedText || match.confidence === 'prefix') return false;
  const matchStart = fullText.indexOf(match.matchedText);
  if (matchStart === -1) return false;
  const ranges = changeRanges(match.matchedText, changes);
  if (!ranges) return false;

  // Text offset -> DOM position (the end of a run, at a run boundary)
  const positionAt = (index: number): { node: Text; offset: number } | null => {
    let offset = 0;
    for (const node of textNodes) {
      const len = (node.textContent || '').length;
      if (index <= offset + len) return { node, offset: index - offset };
      offset += len;
    }
    return null;
  };

  // docx-preview puts each run's formatting (font, bold, underline) on its
  // span: inserted words go in a copy of a run, or they'd take the app's font
  const runOf = (node: Text): HTMLElement | null => {
    const runEl = node.parentElement;
    return runEl && runEl !== element && element.contains(runEl) ? runEl : null;
  };
  const spans: Array<{ node: Text; start: number; end: number }> = [];
  let spanStart = 0;
  for (const node of textNodes) {
    const end = spanStart + (node.textContent || '').length;
    spans.push({ node, start: spanStart, end });
    spanStart = end;
  }
  // Formatting the inserted words take, as the redline does: new defined terms
  // like the paragraph's first quoted term, a new clause heading like the
  // heading it replaces, other words like most of the replaced text
  const term = spans.find(
    s => !s.node.parentElement?.closest('del.tracked-del') && isQuotedTerm(fullText, s.start, s.end),
  );
  const termRun = term ? runOf(term.node) : null;
  const replacedRuns = (start: number, end: number) => {
    const overlapping = spans
      .map(s => ({ node: s.node, from: Math.max(s.start, start), to: Math.min(s.end, end) }))
      .filter(s => s.to > s.from);
    if (overlapping.length === 0) return { body: null, heading: null };
    let dominant = overlapping[0];
    for (const s of overlapping) if (s.to - s.from > dominant.to - dominant.from) dominant = s;
    const first = overlapping[0];
    const heading = isClauseHeading(fullText.slice(first.from, first.to)) ? runOf(first.node) : null;
    return { body: runOf(dominant.node), heading };
  };

  // Live DOM ranges first, applied last to first so offsets stay valid
  const planned: Array<{ range: Range; insert?: Array<{ text: string; run: HTMLElement | null }> }> = [];
  let replaced: { start: number; end: number } | null = null;
  for (const change of ranges) {
    const from = positionAt(matchStart + (change.type === 'delete' ? change.start : change.at));
    const to = change.type === 'delete' ? positionAt(matchStart + change.end) : from;
    if (!from || !to) return false;
    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    if (change.type === 'insert') {
      const formats =
        replaced && replaced.end === matchStart + change.at
          ? replacedRuns(replaced.start, replaced.end)
          : { body: runOf(from.node), heading: null };
      const pieces = insertRoles(change.text, { heading: !!formats.heading, term: !!termRun }).map(piece => ({
        text: piece.text,
        run: piece.role === 'heading' ? formats.heading : piece.role === 'term' ? termRun : formats.body,
      }));
      planned.push({ range, insert: pieces });
      replaced = null;
    } else {
      planned.push({ range });
      replaced = { start: matchStart + change.start, end: matchStart + change.end };
    }
  }

  for (const { range, insert } of planned.reverse()) {
    if (insert) {
      const content = document.createDocumentFragment();
      for (const piece of insert) {
        const runCopy = piece.run ? (piece.run.cloneNode(false) as HTMLElement) : null;
        if (runCopy) runCopy.textContent = piece.text;
        content.appendChild(runCopy ?? document.createTextNode(piece.text));
      }
      let node: Node = content;
      if (markup) {
        const ins = document.createElement('ins');
        ins.className = 'tracked-ins';
        ins.appendChild(content);
        node = ins;
      }
      range.insertNode(node);
    } else if (markup) {
      const del = document.createElement('del');
      del.className = 'tracked-del';
      del.appendChild(range.extractContents());
      range.insertNode(del);
    } else {
      range.deleteContents();
    }
  }
  return true;
}

const MARKUP_KEY = 'queri.viewer.markup';

// --- Main component ---

export const SuggestionDocumentViewer: React.FC<SuggestionDocumentViewerProps> = ({
  fileUrl,
  suggestions,
  scrollToSuggestionId,
  onScrollComplete,
  onAcceptSuggestion,
  onRejectSuggestion,
  onUndoSuggestion,
  onSuggestionsApplyFailed,
  disabled = false,
}) => {
  const { t } = useI18n();
  const dv = t.qagentDocument.documentViewer;
  const DOCX_PAGE_WIDTH = 816;
  const ZOOM_MIN = 0.3;
  const ZOOM_MAX = 1.5;
  const ZOOM_STEP = 0.1;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rendered, setRendered] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [showChanges, setShowChanges] = useState(false);
  // Accepted changes shown as tracked changes (default) or applied cleanly
  const [showMarkup, setShowMarkup] = useState(() => {
    try {
      return localStorage.getItem(MARKUP_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const toggleMarkup = useCallback(() => {
    setShowMarkup(prev => {
      try {
        localStorage.setItem(MARKUP_KEY, prev ? 'off' : 'on');
      } catch {
        // Preference only; the toggle still works without storage
      }
      return !prev;
    });
  }, []);
  // Store the original rendered HTML so we can reset the DOM on undo
  const originalHtmlRef = useRef<string>('');
  const [zoom, setZoom] = useState(0.6);
  const [autoZoom, setAutoZoom] = useState(0.6);

  const zoomIn = useCallback(() => setZoom(z => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 10) / 10)), []);
  const zoomOut = useCallback(() => setZoom(z => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 10) / 10)), []);
  const zoomFit = useCallback(() => setZoom(autoZoom), [autoZoom]);
  const zoomLabel = useMemo(() => `${Math.round(zoom * 100)}%`, [zoom]);
  const [portalTargets, setPortalTargets] = useState<Map<number, HTMLDivElement>>(new Map());
  const [resolvedSuggestionsByIdx, setResolvedSuggestionsByIdx] = useState<Record<number, QAgentSuggestion[]>>({});

  const docContainerRef = useRef<HTMLDivElement>(null);
  const scaledHeight = useScaledHeight(docContainerRef, zoom);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLDivElement>(null);

  // Calculate fit-to-width zoom based on container size
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width;
        const fitZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX,
          (containerWidth - 32) / DOCX_PAGE_WIDTH
        ));
        const rounded = Math.round(fitZoom * 20) / 20;
        setAutoZoom(rounded);
        setZoom(rounded);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const paragraphRefs = useRef<Map<number, HTMLElement>>(new Map());
  const appliedSuggestionsRef = useRef<Set<string>>(new Set());
  const listDefsRef = useRef<ListDefinitions | null>(null);
  // Ids we've already warned the user about, so a failed apply is reported once
  // rather than on every re-render. Cleared when the document reloads.
  const applyFailuresNotifiedRef = useRef<Set<string>>(new Set());

  // Render DOCX with docx-preview
  useEffect(() => {
    const loadDocument = async () => {
      if (!docContainerRef.current) return;

      try {
        setLoading(true);
        setError(null);
        setRendered(false);
        appliedSuggestionsRef.current = new Set();
        applyFailuresNotifiedRef.current = new Set();

        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch document');
        }

        let blob = await response.blob();

        // Number lists as Word does (see word-numbering): give paragraphs
        // numbered by their style that list explicitly, and keep the list
        // definitions for the counting pass. On any problem, render as is.
        listDefsRef.current = null;
        try {
          const zip = await JSZip.loadAsync(blob);
          const numberingXml = await zip.file('word/numbering.xml')?.async('text');
          const stylesXml = await zip.file('word/styles.xml')?.async('text');
          const documentXml = await zip.file('word/document.xml')?.async('text');
          if (numberingXml) {
            const defs = parseNumbering(numberingXml);
            if (stylesXml && documentXml) {
              const numbered = withParagraphNumbering(documentXml, stylesXml, defs);
              if (numbered !== documentXml) {
                zip.file('word/document.xml', numbered);
                blob = await zip.generateAsync({ type: 'blob' });
              }
            }
            listDefsRef.current = defs;
          }
        } catch (err) {
          console.warn('[SuggestionViewer] List numbering fix skipped:', err);
          listDefsRef.current = null;
        }

        // Clear previous content
        docContainerRef.current.innerHTML = '';

        await renderAsync(blob, docContainerRef.current, styleRef.current ?? undefined, {
          className: 'docx-preview',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          renderComments: false,
          renderChanges: false,
        });

        // Number styles for every instance of each list (docx-preview only
        // generates them for one; see word-numbering)
        if (listDefsRef.current) {
          const styleHost = styleRef.current ?? docContainerRef.current;
          const generated = Array.from(styleHost.querySelectorAll('style'))
            .map(el => el.textContent ?? '')
            .join('\n');
          const extra = listStylesForAllInstances(generated, listDefsRef.current);
          if (extra) {
            const el = document.createElement('style');
            el.textContent = extra;
            styleHost.appendChild(el);
          }
        }

        // Capture the pristine HTML before any suggestion modifications
        originalHtmlRef.current = docContainerRef.current.innerHTML;
        setRendered(true);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(err instanceof Error ? err.message : 'Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [fileUrl]);

  // Post-render: index paragraphs, inject portal targets, highlight
  // Reset DOM to original on every run so undo/re-accept works cleanly.
  useEffect(() => {
    const container = docContainerRef.current;
    if (!container || !rendered || loading) return;

    // Reset DOM to pristine state before applying any suggestions.
    // This ensures undo works: we always start from the original document.
    if (originalHtmlRef.current) {
      container.innerHTML = originalHtmlRef.current;
    }
    appliedSuggestionsRef.current = new Set();

    // Query body paragraphs across all pages. docx-preview creates:
    //   <section class="docx-preview"> per page
    //     headers/footers directly in section
    //     <article> for body content
    // We only want <p> inside <article> to match backend paragraph indexing.
    const allParagraphs = container.querySelectorAll('article p');

    // Build index of non-empty paragraphs (matching backend indexing)
    const indexedParagraphs = new Map<number, HTMLElement>();
    let paragraphIndex = 0;
    allParagraphs.forEach((p) => {
      const text = p.textContent?.trim() || '';
      if (text) {
        indexedParagraphs.set(paragraphIndex, p as HTMLElement);
        paragraphIndex++;
      }
    });

    console.log(`[SuggestionViewer] Indexed ${indexedParagraphs.size} paragraphs from docx-preview`);

    // Determine which paragraph indices have pending suggestions
    const pendingIndices = new Set<number>();
    const resolvedByIdx: Record<number, QAgentSuggestion[]> = {};
    if (showSuggestions) {
      for (const s of suggestions) {
        if (s.status === 'pending') {
          const searchText = getSuggestionSearchText(s);
          const foundEl = findParagraphByText(indexedParagraphs, searchText, s.paragraphIndex);
          const resolvedIdx = findIndexForElement(indexedParagraphs, foundEl, s.paragraphIndex);

          if (!foundEl) {
            console.warn(`[SuggestionViewer] MATCH FAILED for ${s.type} suggestion "${s.id.slice(0, 8)}":`,
              `\n  searchText: "${searchText?.slice(0, 80)}..."`,
              `\n  paragraphIndex hint: ${s.paragraphIndex}`,
              `\n  fallback resolvedIdx: ${resolvedIdx}`,
              `\n  paragraph at hint index: "${indexedParagraphs.get(s.paragraphIndex)?.textContent?.slice(0, 80)}..."`,
            );
          } else {
            console.log(`[SuggestionViewer] Matched ${s.type} suggestion to paragraph ${resolvedIdx}`);
          }

          pendingIndices.add(resolvedIdx);
          if (!resolvedByIdx[resolvedIdx]) resolvedByIdx[resolvedIdx] = [];
          resolvedByIdx[resolvedIdx].push(s);
        }
      }
    }

    // Manage portal targets and highlights
    const nextPortals = new Map<number, HTMLDivElement>();

    indexedParagraphs.forEach((pEl, idx) => {
      if (pendingIndices.has(idx)) {
        // Add highlight
        pEl.classList.add('suggestion-paragraph');
        // The marker sits left of the whole paragraph: a numbered clause's
        // first line hangs out to the left (negative text-indent)
        const indent = parseFloat(getComputedStyle(pEl).textIndent) || 0;
        pEl.style.setProperty('--suggestion-hang', `${Math.min(0, indent)}px`);

        // Reuse existing portal div or create new one
        let portalDiv = pEl.nextElementSibling as HTMLDivElement | null;
        if (!portalDiv || !portalDiv.classList.contains('suggestion-portal')) {
          portalDiv = document.createElement('div');
          portalDiv.className = 'suggestion-portal';
          pEl.parentNode?.insertBefore(portalDiv, pEl.nextSibling);
        }
        nextPortals.set(idx, portalDiv);
      } else {
        // Remove highlight
        pEl.classList.remove('suggestion-paragraph');

        // Remove orphan portal div
        const nextEl = pEl.nextElementSibling;
        if (nextEl && nextEl.classList.contains('suggestion-portal')) {
          nextEl.remove();
        }
      }
    });

    // Apply accepted suggestion edits to the DOM. Collect any that can't be
    // placed so we can warn the user instead of silently showing "Accepted".
    // With markup on, deleted paragraphs stay visible struck through and
    // inserted ones are underlined, as tracked changes in Word.
    const removeParagraph = (el: HTMLElement) => {
      if (showMarkup) el.classList.add('tracked-del-paragraph');
      else el.style.display = 'none';
    };
    const failedToApply: string[] = [];
    for (const s of suggestions) {
      if (s.status !== 'accepted') continue;
      if (appliedSuggestionsRef.current.has(s.id)) continue;

      const searchText = getSuggestionSearchText(s);
      const pEl = findParagraphByText(indexedParagraphs, searchText, s.paragraphIndex)
        || indexedParagraphs.get(s.paragraphIndex);
      if (!pEl) {
        console.warn(`[SuggestionViewer] Cannot apply accepted ${s.type} "${s.id.slice(0, 8)}": paragraph not found`,
          `\n  searchText: "${searchText?.slice(0, 80)}..."`,
          `\n  paragraphIndex: ${s.paragraphIndex}`,
        );
        failedToApply.push(s.id);
        continue;
      }

      if (s.type === 'edit' && s.oldText && s.newText) {
        const applied = showMarkup
          // The redline's own changes when the backend sent them; otherwise
          // (older data) the viewer's approximation
          ? (s.changes && applyCardChangesInElement(pEl, s.oldText, s.changes, true)) ||
            markTextChangeInElement(pEl, s.oldText, s.newText)
          : (s.changes && applyCardChangesInElement(pEl, s.oldText, s.changes, false)) ||
            replaceTextInElement(pEl, s.oldText, s.newText);
        if (applied) {
          appliedSuggestionsRef.current.add(s.id);
        } else {
          console.warn(`[SuggestionViewer] Cannot apply accepted edit "${s.id.slice(0, 8)}": oldText not located in paragraph`,
            `\n  oldText: "${s.oldText.slice(0, 80)}..."`,
          );
          failedToApply.push(s.id);
        }
      } else if (s.type === 'delete') {
        // Exactly the paragraph the backend removes, as in the Word export:
        // no hiding of the clause's following paragraphs or renumbering here,
        // or the preview would show a redline the download doesn't have
        removeParagraph(pEl);
        appliedSuggestionsRef.current.add(s.id);
      } else if (s.type === 'insert' && s.newText) {
        // Backend inserts new content after the target paragraph.
        // The newText may contain a heading + body separated by newlines.
        // Split into separate paragraphs, find a style donor at the matching
        // heading level, deep-clone it, and replace just the text.

        // Split on newlines, filter empty
        const lines = s.newText
          .split(/\n\n+/)
          .flatMap(block => block.split(/\n/))
          .map(l => l.trim())
          .filter(l => l.length > 0);

        // After the paragraph, its portal div, and earlier inserts at this
        // same paragraph, so several new clauses keep their order
        let insertAfter: Element = insertionPointAfter(pEl);

        for (const line of lines) {
          const level = headingLevel(line);
          let donor: HTMLElement | null = null;

          if (level > 0) {
            // This line is a heading - find a donor heading at the same level
            donor = findHeadingDonor(indexedParagraphs, level, s.paragraphIndex);
          }
          if (!donor) {
            // Body text or no matching heading found - use a body paragraph
            donor = findBodyDonor(indexedParagraphs, s.paragraphIndex) || pEl;
          }

          const newP = cloneParagraphWithNewText(donor, line);
          markInsertedAfter(newP, pEl);
          if (showMarkup) newP.classList.add('tracked-ins-paragraph');
          insertAfter.parentNode?.insertBefore(newP, insertAfter.nextSibling);
          insertAfter = newP;
        }
        // Following clauses keep their numbers, as in the Word export
        appliedSuggestionsRef.current.add(s.id);
      }
    }

    // List numbers as Word shows them, counting inserted and leaving out
    // hidden (deleted) paragraphs
    if (listDefsRef.current) applyWordListNumbers(container, listDefsRef.current);

    // Surface any accepted suggestions we couldn't place, once per suggestion,
    // so the user isn't left thinking a change applied when it didn't.
    if (failedToApply.length > 0 && onSuggestionsApplyFailed) {
      const fresh = failedToApply.filter(id => !applyFailuresNotifiedRef.current.has(id));
      if (fresh.length > 0) {
        fresh.forEach(id => applyFailuresNotifiedRef.current.add(id));
        onSuggestionsApplyFailed(fresh);
      }
    }

    // Store paragraph refs for scroll targeting
    paragraphRefs.current = indexedParagraphs;
    setPortalTargets(nextPortals);
    setResolvedSuggestionsByIdx(resolvedByIdx);
  }, [rendered, loading, suggestions, showSuggestions, showMarkup, onSuggestionsApplyFailed]);

  // Scroll to suggestion
  useEffect(() => {
    if (!scrollToSuggestionId || loading || !rendered) return;

    const suggestion = suggestions.find(s => s.id === scrollToSuggestionId);
    if (!suggestion) {
      onScrollComplete?.();
      return;
    }

    const searchText = getSuggestionSearchText(suggestion);
    const paragraphEl = findParagraphByText(paragraphRefs.current, searchText, suggestion.paragraphIndex)
      || paragraphRefs.current.get(suggestion.paragraphIndex);
    if (paragraphEl) {
      setTimeout(() => {
        paragraphEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        paragraphEl.classList.add('suggestion-flash');
        setTimeout(() => paragraphEl.classList.remove('suggestion-flash'), 1500);
        onScrollComplete?.();
      }, 100);
    } else {
      onScrollComplete?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToSuggestionId, loading, rendered]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <FileText className="h-8 w-8" />
          <span className="text-destructive">{dv.loadFailed}</span>
        </div>
      </div>
    );
  }

  const pendingSuggestions = suggestions.filter(s => s.status === 'pending');
  const acceptedChanges = suggestions.filter(s => s.status === 'accepted' && !s.exportedAt);
  const hasSuggestions = suggestions.length > 0;

  return (
    <div className="suggestion-doc-container h-full w-full flex flex-col bg-white overflow-hidden">
      {/* Hidden container for docx-preview injected styles */}
      <div ref={styleRef} style={{ display: 'none' }} />

      {/* Toolbar */}
      {!loading && (
        <div className="flex-shrink-0 border-b px-4 py-2 flex items-center justify-between bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {hasSuggestions && pendingSuggestions.length > 0 ? (
              <span>
                <span className="font-medium text-brand">{pendingSuggestions.length}</span>{dv.pendingSuggestions(pendingSuggestions.length)}
              </span>
            ) : hasSuggestions ? (
              <span>{dv.allReviewed}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <button onClick={zoomOut} className="p-1 rounded hover:bg-muted text-muted-foreground" title={dv.zoomOut}>
                <ZoomOut className="h-3.5 w-3.5" />
              </button>
              <button onClick={zoomFit} className="px-1 py-0.5 rounded hover:bg-muted" title={dv.fitToWidth}>
                <span className="text-xs text-muted-foreground w-10 text-center select-none">{zoomLabel}</span>
              </button>
              <button onClick={zoomIn} className="p-1 rounded hover:bg-muted text-muted-foreground" title={dv.zoomIn}>
                <ZoomIn className="h-3.5 w-3.5" />
              </button>
            </div>
            {acceptedChanges.length > 0 && (
              <Button
                variant={showMarkup ? "secondary" : "ghost"}
                size="sm"
                onClick={toggleMarkup}
                className="gap-1.5"
                title={dv.markupTooltip}
                aria-pressed={showMarkup}
              >
                <PenLine className="h-3.5 w-3.5" />
                <span className="text-xs">{dv.markup}</span>
              </Button>
            )}
            {acceptedChanges.length > 0 && (
              <Button
                variant={showChanges ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setShowChanges(!showChanges)}
                className="gap-1.5"
              >
                <GitCompare className="h-3.5 w-3.5" />
                <span className="text-xs">{dv.changes}</span>
                <Badge variant="secondary" className="h-4 px-1 text-[10px] min-w-[16px] justify-center">
                  {acceptedChanges.length}
                </Badge>
              </Button>
            )}
            {hasSuggestions && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSuggestions(!showSuggestions)}
                className="gap-2"
              >
                {showSuggestions ? (
                  <>
                    <EyeOff className="h-4 w-4" />
                    <span className="text-xs">{dv.hide}</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    <span className="text-xs">{dv.show}</span>
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Document content + Changes panel */}
      <div className="flex-1 min-h-0 relative flex">
        <div ref={scrollContainerRef} className={cn(
          "absolute inset-0 overflow-auto",
          showChanges && "right-[280px]"
        )}>
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span>{dv.loading}</span>
              </div>
            </div>
          )}
          {/* Sized to the scaled content so scrolling ends at the last page */}
          <div style={{ height: scaledHeight, overflow: 'hidden' }}>
            <div
              ref={docContainerRef}
              className="docx-viewer-container"
              style={{
                display: loading ? 'none' : undefined,
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
              }}
            />
          </div>
        </div>

        {/* What Changed panel */}
        {showChanges && (
          <div className="absolute right-0 top-0 bottom-0 w-[280px] border-l bg-background overflow-y-auto">
            <div className="p-3 border-b">
              <h3 className="text-sm font-medium">{dv.acceptedChanges}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {dv.notExported(acceptedChanges.length)}
              </p>
            </div>
            <div className="p-2 space-y-2">
              {acceptedChanges.map((change) => (
                <div key={change.id} className="border rounded-md p-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className={cn("text-[10px]", {
                      'bg-blue-50 text-blue-700 border-blue-200': change.type === 'edit',
                      'bg-green-50 text-green-700 border-green-200': change.type === 'insert',
                      'bg-red-50 text-red-700 border-red-200': change.type === 'delete',
                    })}>
                      {t.qagentDocument.suggestions.types[change.type] ?? change.type}
                    </Badge>
                    {change.sectionTitle && (
                      <span className="text-[10px] text-muted-foreground truncate max-w-[120px]">
                        {change.sectionTitle}
                      </span>
                    )}
                  </div>
                  {change.oldText && (
                    <div className="text-xs bg-red-50 rounded px-1.5 py-1 border border-red-100">
                      <span className="text-red-600 line-through">{change.oldText.slice(0, 100)}{change.oldText.length > 100 ? '...' : ''}</span>
                    </div>
                  )}
                  {change.newText && (
                    <div className="text-xs bg-green-50 rounded px-1.5 py-1 border border-green-100">
                      <span className="text-green-600">{change.newText.slice(0, 100)}{change.newText.length > 100 ? '...' : ''}</span>
                    </div>
                  )}
                  {change.comment && (
                    <p className="text-[10px] text-muted-foreground">{change.comment}</p>
                  )}
                  {onUndoSuggestion && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onUndoSuggestion(change.id)}
                      className="h-6 gap-1 text-[10px] text-amber-600 hover:bg-amber-50 p-0 px-1.5"
                    >
                      <RotateCcw className="h-3 w-3" />
                      {t.qagentDocument.suggestions.undo}
                    </Button>
                  )}
                </div>
              ))}
              {acceptedChanges.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {dv.noAcceptedChanges}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Render suggestion cards into portal targets */}
      {Array.from(portalTargets.entries()).map(([idx, el]) =>
        createPortal(
          <SuggestionCards
            key={idx}
            suggestions={resolvedSuggestionsByIdx[idx] || []}
            onAccept={onAcceptSuggestion}
            onReject={onRejectSuggestion}
            disabled={disabled}
          />,
          el,
        ),
      )}

      <style>{`
        .suggestion-doc-container .docx-preview-wrapper {
          background: hsl(var(--muted) / 0.3);
          padding: 16px;
        }
        .suggestion-doc-container .docx-preview-wrapper section.docx {
          background: white;
          box-shadow: 0 1px 4px rgba(0,0,0,0.1);
          margin-bottom: 16px;
          border-radius: 2px;
        }
        .suggestion-paragraph {
          position: relative;
        }
        /* A bar beside the paragraph, outside its text, so the layout stays
           as in Word (no border or padding on the paragraph itself) */
        .suggestion-paragraph::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: calc(var(--suggestion-hang, 0px) - 10px);
          width: 4px;
          border-radius: 2px;
          background: hsl(var(--brand));
        }
        .suggestion-portal {
          margin: 8px 16px;
        }
        .suggestion-doc-container del.tracked-del,
        .suggestion-doc-container del.tracked-del *,
        .suggestion-doc-container .tracked-del-paragraph,
        .suggestion-doc-container .tracked-del-paragraph * {
          color: #b91c1c !important;
          text-decoration: line-through !important;
        }
        .suggestion-doc-container ins.tracked-ins,
        .suggestion-doc-container ins.tracked-ins *,
        .suggestion-doc-container .tracked-ins-paragraph,
        .suggestion-doc-container .tracked-ins-paragraph * {
          color: #15803d !important;
          text-decoration: underline !important;
        }
        .suggestion-flash {
          animation: suggestionFlash 1.5s ease-out;
        }
        @keyframes suggestionFlash {
          0%, 30% { background-color: rgba(var(--brand-rgb, 34, 197, 94), 0.3); }
          100% { background-color: transparent; }
        }
      `}</style>
    </div>
  );
};

export default SuggestionDocumentViewer;
